import {
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";

import { useAuth } from "@/providers";
import { RobotoLoadingButton } from "@/shared/components/RobotoLoadingButton";
import { useOrgConfig, useUpdateOrgConfig } from "@/shared/domain/config/hooks";
import { CloseIcon } from "@/shared/foundation/icons/mui";
import { Button } from "@/shared/foundation/primitives";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import styles from "./AIContextSettingsModal.module.css";

interface AIContextSettingsModalProps {
  /** Whether the dialog is currently open. */
  open: boolean;
  /** Callback invoked when the user dismisses the dialog. */
  onClose: () => void;
}

export function AIContextSettingsModal({
  open,
  onClose,
}: AIContextSettingsModalProps) {
  const { currentOrganization } = useAuth();
  const updateOrgConfig = useUpdateOrgConfig();

  const [commonKnowledge, setCommonKnowledge] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const commonKnowledgeQuery = useOrgConfig<string>(
    currentOrganization?.org_id,
    "ai_common_knowledge",
  );

  useEffect(() => {
    if (!commonKnowledgeQuery.isLoading && !hasChanges) {
      setCommonKnowledge(commonKnowledgeQuery.data || "");
    }
  }, [commonKnowledgeQuery.data, commonKnowledgeQuery.isLoading, hasChanges]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const handleInputChange = (value: string) => {
    setCommonKnowledge(value);
    setHasChanges(true);
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    if (!currentOrganization?.org_id) {
      return;
    }

    setErrorMessage(null);
    try {
      await updateOrgConfig.mutateAsync({
        orgId: currentOrganization.org_id,
        key: "ai_common_knowledge",
        value: commonKnowledge,
      });

      setHasChanges(false);
      setSuccessMessage("AI settings saved successfully!");
      successTimerRef.current = setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      ErrorMonitoringService.captureError(
        error instanceof Error
          ? error
          : new Error("Failed to save AI settings"),
      );
      setErrorMessage("Failed to save AI settings. Please try again.");
    }
  };

  const handleClose = () => {
    setCommonKnowledge(commonKnowledgeQuery.data || "");
    setHasChanges(false);
    setSuccessMessage(null);
    setErrorMessage(null);
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle className={styles.dialogTitle}>
        AI Context Settings
        <IconButton
          aria-label="Close AI context settings"
          onClick={handleClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {commonKnowledgeQuery.isLoading && (
        <div className={styles.dialogContentCentered}>
          <CircularProgress size="1.5rem" />
        </div>
      )}

      {!commonKnowledgeQuery.isLoading && commonKnowledgeQuery.error && (
        <div className={styles.dialogContent}>
          <Alert severity="error">
            Failed to load AI settings. Please try again.
          </Alert>
        </div>
      )}

      {!commonKnowledgeQuery.isLoading && !commonKnowledgeQuery.error && (
        <>
          <div className={styles.dialogContent}>
            {successMessage && (
              <Alert severity="success">{successMessage}</Alert>
            )}

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            <div className={styles.section}>
              <span className={styles.sectionTitle}>Common Knowledge</span>
              <span className={styles.sectionDescription}>
                Context provided to all AI-powered features across your
                organization.
              </span>
              <TextField
                fullWidth
                multiline
                minRows={6}
                value={commonKnowledge}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder="Enter human readable instructions as text or markdown"
                variant="outlined"
                size="small"
              />
            </div>
          </div>

          <div className={styles.actions}>
            <Button variant="outlined" onClick={handleClose} size="small">
              Cancel
            </Button>
            <RobotoLoadingButton
              variant="contained"
              onClick={() => {
                void handleSave();
              }}
              disabled={!hasChanges || updateOrgConfig.isPending}
              loading={updateOrgConfig.isPending}
              eventName="AIOrgConfigSaveClicked"
              size="small"
            >
              Save Changes
            </RobotoLoadingButton>
          </div>
        </>
      )}
    </Dialog>
  );
}
